import React, { useRef, useState } from 'react'
import { Prediction } from '../types/prediction'
import { Card } from 'react-bootstrap'
import { useContentImage } from '../hooks/useContentImage'
import { useAppSelector } from '../store/hooks'
import { selectSelectedProjectId } from '../store/slices/projectSlice'
import ImageCard from './annotation/ContentImageCard'
import { FaCheck } from 'react-icons/fa'
import styled from 'styled-components'
import { useGetProjectQuery } from '../services/projectsApi'

interface EmptyPredictionImageProps {
  prediction: Prediction
  onEdit: (prediction: Prediction) => void
  onToggle: (prediction: Prediction) => void
  isSelected: boolean
}

const StyledCard = styled(Card)`
  width: 100%;
  height: 100%;
  cursor: pointer;
  position: relative;
  background: transparent;
  border: none;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
`

const ImageContainer = styled.div`
  width: 100%;
  height: 100%;
  aspect-ratio: 1;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
`

const EmptyPredictionImage: React.FC<EmptyPredictionImageProps> = ({
  prediction,
  onEdit,
  onToggle,
  isSelected,
}) => {
  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const { data: project } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })
  const [isHovered, setIsHovered] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const { base64Image, imageLoading, imageRef, contentData } = useContentImage({
    contentId: prediction.contentid,
    projectId: selectedProjectId || '',
    datasetId: project?.datasetid || '',
  })

  const handleClick = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.metaKey) {
      onToggle(prediction)
    } else {
      onEdit(prediction)
    }
  }

  return (
    <StyledCard
      onClick={handleClick}
      className={isSelected ? 'selected' : ''}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <ImageContainer ref={containerRef}>
        <div ref={imageRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
          {imageLoading && (
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 1,
              }}
            >
              <div className="spinner-border spinner-border-sm" />
            </div>
          )}
          {base64Image && contentData && (
            <ImageCard
              base64Data={base64Image}
              bounding_boxes={[]}
              contentWidth={contentData.width}
              contentHeight={contentData.height}
              background="linear-gradient(135deg, #1a1a1a 0%, #252525 100%)"
              isSelected={isSelected}
              isHovered={isHovered}
            />
          )}
        </div>
        {isSelected && (
          <div
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              background: 'linear-gradient(135deg, #9333ea 0%, #c084fc 100%)',
              borderRadius: '50%',
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(147, 51, 234, 0.4)',
              border: '2px solid #ffffff',
              zIndex: 3,
            }}
          >
            <FaCheck color="white" size={14} />
          </div>
        )}
      </ImageContainer>
    </StyledCard>
  )
}

export default EmptyPredictionImage
